import { NodeSize } from 'Automations/automationEditor/nodeExpansion.atoms'
import { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import { NodeType } from 'Automations/nodeDefinitions/types'

export type NodeOffset = { dx: number; dy: number }

type Axis = 'x' | 'y'
type Dimension = 'width' | 'height'

const AXES: { axis: Axis; dimension: Dimension }[] = [
  { axis: 'x', dimension: 'width' },
  { axis: 'y', dimension: 'height' }
]

/**
 * Computes the ephemeral, gap-preserving layout for an expanded canvas.
 *
 * Node positions are fixed/manual, so when a node grows it would overlap its
 * neighbours. Expansion is anchored at each node's centre: a growing node keeps
 * its centre fixed and grows symmetrically in every direction, pushing the
 * neighbours on both sides clear by half its growth each. For every axis we run
 * the same edge-to-edge-gap-preserving sweep in both directions — forward (each
 * node pushed clear of the grown far edge of preceding nodes) and backward (each
 * node pushed clear of the grown near edge of following nodes) — and take the
 * centre shift as half their difference, so a node feels equal push from growers
 * on either side. A node is pushed along an axis only when a grower is separated
 * from it along that axis *and* the grower's expansion reaches its perpendicular
 * band — so a straight horizontal row spreads only horizontally and a vertical
 * branch only vertically, while a diagonally offset node (clear of the grower on
 * both axes when collapsed) is pushed on both axes once the grower grows large
 * enough to reach it, instead of being swallowed. The growing node shifts by
 * `-Δsize/2` on each axis so that, once it grows from its top-left DOM corner,
 * its visual centre stays fixed.
 *
 * Inputs are always presentation-space (collapsed-layout) positions and the
 * *settled* target sizes, so the offsets are computed once per toggle rather
 * than re-derived from the live, animating size. A node with a missing
 * collapsed size can't be placed, so it's left where it is (Δ = 0); an expanded
 * node with a missing expanded size also gets Δ = 0.
 */
export const applyExpandedLayout = ({
  nodes,
  expandedNodeTypes,
  collapsedSizes,
  expandedSizes
}: {
  nodes: AutomationsPresentationV1Node[]
  expandedNodeTypes: Set<NodeType>
  collapsedSizes: Record<string, NodeSize>
  expandedSizes: Record<string, NodeSize>
}): {
  nodes: AutomationsPresentationV1Node[]
  offsets: Record<string, NodeOffset>
} => {
  const sizeDelta = (node: AutomationsPresentationV1Node): NodeSize => {
    const isExpanded = expandedNodeTypes.has(node.type as NodeType)
    const collapsed = collapsedSizes[node.id]
    const expanded = expandedSizes[node.id]

    if (!isExpanded || !collapsed || !expanded) {
      return { width: 0, height: 0 }
    }

    return {
      width: expanded.width - collapsed.width,
      height: expanded.height - collapsed.height
    }
  }

  const offsets = AXES.reduce<Record<string, NodeOffset>>(
    (acc, { axis, dimension }, index) => {
      const perpendicular = AXES[(index + 1) % AXES.length]
      const sweepParams = {
        nodes,
        axis,
        dimension,
        perpAxis: perpendicular.axis,
        perpDimension: perpendicular.dimension,
        collapsedSizes,
        sizeDelta
      }
      const forward = sweepAxis({ ...sweepParams, direction: 'forward' })
      const backward = sweepAxis({ ...sweepParams, direction: 'backward' })

      return nodes
        .filter(node => collapsedSizes[node.id])
        .reduce((nextOffsets, node) => {
          const centreShift = (forward[node.id] - backward[node.id]) / 2
          const offset = nextOffsets[node.id] ?? { dx: 0, dy: 0 }
          return {
            ...nextOffsets,
            [node.id]: {
              ...offset,
              [axis === 'x' ? 'dx' : 'dy']:
                centreShift - sizeDelta(node)[dimension] / 2
            }
          }
        }, acc)
    },
    {}
  )

  const positionedNodes = nodes.map(node => {
    const offset = offsets[node.id]
    if (!offset) return node
    return {
      ...node,
      position: {
        x: node.position.x + offset.dx,
        y: node.position.y + offset.dy
      }
    }
  })

  return { nodes: positionedNodes, offsets }
}

/**
 * Single-direction, gap-preserving sweep for one axis. Returns, per node with a
 * known collapsed size, the magnitude it must move to stay clear of the growing
 * nodes ahead of it in the sweep order.
 *
 * `'forward'` processes nodes by their near (top/left) edge ascending: each node
 * is displaced by the maximum any *preceding* node requires to keep the original
 * edge-to-edge gap — that node's own push plus its full size delta, since it
 * grows toward this one. `'backward'` is the mirror: near edge descending,
 * pushing each node clear of the growers that follow it. The caller combines the
 * two directions into a centre shift, so this function has no self-centring term.
 *
 * A preceding node only pushes this one when (a) the two are *separated* along
 * the sweep axis — one is clearly ahead of the other, not merely stacked — and
 * (b) their *grown* perpendicular spans overlap, i.e. the grower's perpendicular
 * growth reaches this node's band. Condition (b) uses the grown span (each node
 * expands about its centre, so its perpendicular extent grows by half its delta
 * on each side) rather than the collapsed span: that's what lets a diagonally
 * offset node — clear of the grower on both axes when collapsed — be pushed on
 * both axes once the grower expands far enough to reach it, while a straight
 * perpendicular row (separated only on the perpendicular axis) never cascades.
 */
const sweepAxis = ({
  nodes,
  axis,
  dimension,
  perpAxis,
  perpDimension,
  collapsedSizes,
  sizeDelta,
  direction
}: {
  nodes: AutomationsPresentationV1Node[]
  axis: Axis
  dimension: Dimension
  perpAxis: Axis
  perpDimension: Dimension
  collapsedSizes: Record<string, NodeSize>
  sizeDelta: (node: AutomationsPresentationV1Node) => NodeSize
  direction: 'forward' | 'backward'
}): Record<string, number> => {
  const orientation = direction === 'forward' ? 1 : -1
  const placeable = nodes
    .filter(node => collapsedSizes[node.id])
    .map(node => {
      const collapsed = collapsedSizes[node.id]
      const near = node.position[axis]
      const perpStart = node.position[perpAxis]
      const perpDeltaHalf = sizeDelta(node)[perpDimension] / 2
      return {
        id: node.id,
        near,
        far: near + collapsed[dimension],
        delta: sizeDelta(node)[dimension],
        perpStart: perpStart - perpDeltaHalf,
        perpEnd: perpStart + collapsed[perpDimension] + perpDeltaHalf
      }
    })
    .sort((a, b) => orientation * (a.near - b.near))

  type Placeable = (typeof placeable)[number]
  const pushesAlong = (ahead: Placeable, behind: Placeable): boolean => {
    const separated =
      direction === 'forward'
        ? ahead.far <= behind.near
        : behind.far <= ahead.near
    const grownPerpOverlap =
      ahead.perpStart < behind.perpEnd && behind.perpStart < ahead.perpEnd
    return separated && grownPerpOverlap
  }

  return placeable.reduce(
    (acc, node) => {
      const push = acc.resolved.reduce(
        (max, previous) =>
          pushesAlong(previous, node)
            ? Math.max(max, previous.push + previous.delta)
            : max,
        0
      )

      return {
        resolved: [...acc.resolved, { ...node, push }],
        pushes: { ...acc.pushes, [node.id]: push }
      }
    },
    {
      resolved: [] as (Placeable & { push: number })[],
      pushes: {} as Record<string, number>
    }
  ).pushes
}
